import useResizeObserver from '@react-hook/resize-observer';
import { Loader } from 'components';
import { SelectedLocationContext } from 'contexts/selected-location';
import * as d3 from 'd3';
import {
  Dispatch,
  FC,
  ReactNode,
  RefObject,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useNavigate } from 'react-router';

import fetchWithRetry from 'services/fetch-retry';
import { Customer } from 'types';
import { Filter, FilterOperator, LogicalOperator } from 'utils/rsql-util';

// const colorScheme = [
//   '#e6194b',
//   '#f58231',
//   '#ffe119',
//   '#bfef45',
//   '#3cb44b',
//   '#aaffc3',
//   '#42d4f4',
//   '#4363d8',
//   '#000075',
//   '#911eb4',
//   '#f032e6',
//   '#f9adf5'
// ];

const FONT_SIZE = 12;
const MARGIN = 24;
const MARGIN_LEFT = Math.min(156, window.innerWidth / 5);

/* preserve original customer name and unit counts in children (list for OTHER) */
type BarChartData = {
  customerName: string;
  loadedCount: number;
  unitCount: number;
  totalCount: number /* same as unit count unless filtered */;
  children: Omit<BarChartData, 'children'>[];
};

const CustomerUnitMap: FC<{
  boxRef: RefObject<HTMLDivElement>;
  focused?: boolean;
  setTitleElement?: Dispatch<SetStateAction<ReactNode>>;
}> = ({ boxRef, focused, setTitleElement }) => {
  const navigate = useNavigate();
  const { selectedLocation } = useContext(SelectedLocationContext);

  const [counts, setCounts] = useState<
    { customer: Customer; loadedCount: number; unitCount: number; totalCount: number }[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [maxBars] = useState<number>(12);
  const [loadedOnly, setLoadedOnly] = useState<boolean>(false);
  const svgRef = useRef<SVGSVGElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const [{ width, height }, setDimensions] = useState({ height: 0, width: 0 });
  useLayoutEffect(() => {
    setDimensions(() => ({
      width: boxRef.current?.clientWidth ?? 0,
      height: boxRef.current?.clientHeight ?? 0
    }));
  }, [boxRef]);
  useResizeObserver(boxRef.current, ({ contentBoxSize }) => {
    setDimensions({
      width: contentBoxSize[0].inlineSize,
      height: contentBoxSize[0].blockSize
    });
  });

  const getCounts = useCallback(async () => {
    setLoading(true);
    const url = new URL(`/api/units/counts/customer?location=${selectedLocation?.id}`, window.location.origin);
    setCounts(await fetchWithRetry<typeof counts>(url, { method: 'GET' }, []));
    setLoading(false);
  }, [selectedLocation?.id]);

  useEffect(() => {
    getCounts();
  }, [getCounts]);

  useEffect(
    () =>
      setTitleElement?.(
        <div className="d-flex align-items-start g-2">
          <span>LOADED ONLY</span>
          <label className="switch">
            <input type="checkbox" checked={loadedOnly} onChange={e => setLoadedOnly(e.target.checked)} />
            <span className="slider round" />
          </label>
        </div>
      ),
    [loadedOnly, setLoadedOnly, setTitleElement]
  );

  const data: BarChartData[] = useMemo(() => {
    const sorted: typeof counts = [...counts]
      .map(c => ({ ...c, totalCount: c.unitCount, unitCount: loadedOnly ? c.loadedCount : c.unitCount }))
      .sort((c0, c1) => c1.unitCount - c0.unitCount);
    const other: BarChartData = sorted.splice(maxBars - 1, sorted.length).reduce(
      (acc, c) => {
        acc.loadedCount = acc.loadedCount + c.loadedCount;
        acc.totalCount = acc.totalCount + c.totalCount;
        acc.unitCount = acc.unitCount + c.unitCount;
        acc.children.push({
          customerName: c.customer.name,
          loadedCount: c.loadedCount,
          totalCount: c.totalCount,
          unitCount: c.unitCount
        });
        return acc;
      },
      { customerName: 'OTHER', loadedCount: 0, totalCount: 0, unitCount: 0, children: [] } as BarChartData
    );
    return sorted
      .map(c => ({
        customerName: c.customer.name.replace(/\s?-.*/, ''),
        loadedCount: c.loadedCount,
        totalCount: c.totalCount,
        unitCount: c.unitCount,
        children: [
          {
            customerName: c.customer.name,
            loadedCount: c.loadedCount,
            totalCount: c.totalCount,
            unitCount: c.unitCount
          }
        ]
      }))
      .concat(Array(Number(!!other.unitCount)).fill(other));
  }, [counts, loadedOnly, maxBars]);

  /* include both ungrouped-unit and stack filters, yard-management will apply conditionally based on view */
  const handleClick = useCallback(
    (_: PointerEvent, bar: BarChartData) => {
      const filters: Filter[] = bar.children.reduce((acc, { customerName }) => {
        acc.push({ field: 'customer', value: customerName, filterOperator: FilterOperator.EQUALS }); /* unstacked */
        acc.push({ field: 'chassisCustomerName', value: customerName, filterOperator: FilterOperator.EQUALS });
        acc.push({ field: 'containerCustomerName', value: customerName, filterOperator: FilterOperator.EQUALS });
        acc.push({ field: 'gensetCustomerName', value: customerName, filterOperator: FilterOperator.EQUALS });
        return acc;
      }, [] as Filter[]);
      navigate('/units', { state: { filterState: { logicalOperator: LogicalOperator.OR, filters } } });
    },
    [navigate]
  );

  const renderBarPlot = useCallback(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('g').remove();

    svg
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', [0, 0, width, height])
      .attr('style', `max-width: 100%; height: auto; font-size: ${FONT_SIZE}px`);

    const x = d3
      .scaleLinear()
      .domain([0, Math.max(...data.map(d => d.unitCount))])
      .range([MARGIN_LEFT, width - MARGIN]);

    const y = d3
      .scaleBand()
      .domain(data.map(d => d.customerName))
      .range([MARGIN, height - MARGIN])
      .padding(0.1);

    function handleMouseMove(this: SVGRectElement | null, e: MouseEvent) {
      const size = d3.select(tooltipRef.current).node()?.getBoundingClientRect();
      const ex = Math.max(0, Math.min(width - (size?.width ?? 0), e.offsetX));
      const ey = Math.max(0 + (size?.height ?? 0), Math.min(height, e.offsetY));
      const thisData = (d3.select(this).data()[0] as BarChartData).children;
      d3.select(tooltipRef.current)
        .style('column-count', Math.min(thisData.length, 2))
        .style('max-height', `${height}px`)
        .style('transform', `translate(${ex}px,calc(${ey}px - 100%))`)
        .html(
          thisData.reduce(
            (acc, c) =>
              acc +
              `<div class="d-flex justify-content-between g-2" key="${c.customerName}">
                  <span>${c.customerName}</span>
                  <b>${c.totalCount} (${c.loadedCount} Loaded)</b>
                </div>`,
            ''
          )
        );
    }

    /* append bar for each customer */
    svg
      .append('g')
      .selectAll()
      .data(data)
      .join('rect')
      .attr('x', d => x(d.loadedCount))
      .attr('y', d => y(d.customerName) ?? 0)
      .attr('width', d => x(d.unitCount) - x(d.loadedCount))
      .attr('height', y.bandwidth())
      .style('cursor', 'pointer')
      .attr('class', (_, i) => `text-primary${i % 2 ? '-dark' : '-light'}`)
      .attr('fill', 'currentColor')
      .on('click', handleClick)
      .on('mouseover', () => d3.select(tooltipRef.current).style('opacity', 1))
      .on('mousemove', handleMouseMove)
      .on('mouseleave', () => d3.select(tooltipRef.current).style('opacity', 0));

    /* append loaded bar for each customer */
    svg
      .append('g')
      .selectAll()
      .data(data)
      .join('rect')
      .attr('x', x(0))
      .attr('y', d => y(d.customerName) ?? 0)
      .attr('width', d => x(d.loadedCount) - x(0))
      .attr('height', y.bandwidth())
      .style('cursor', 'pointer')
      .attr('class', 'text-secondary')
      .attr('fill', 'currentColor')
      .on('click', handleClick)
      .on('mouseover', () => d3.select(tooltipRef.current).style('opacity', 1))
      .on('mousemove', handleMouseMove)
      .on('mouseleave', () => d3.select(tooltipRef.current).style('opacity', 0));

    /* append unit count for each bar */
    svg
      .append('g')
      .attr('fill', 'white')
      .attr('text-anchor', 'end')
      .selectAll()
      .data(data)
      .join('text')
      .attr('x', d => x(d.unitCount))
      .attr('y', d => (y(d.customerName) ?? 0) + y.bandwidth() / 2)
      .attr('dy', '0.35em')
      .attr('dx', '-0.25em')
      .attr('pointer-events', 'none')
      .text(d => d.unitCount)
      .call(text =>
        text
          .filter(d => x(d.unitCount) - x(0) < FONT_SIZE * 2) /* short bars */
          .attr('dx', '0.25em')
          .attr('fill', 'black')
          .attr('text-anchor', 'start')
      );

    /* append axes */
    svg
      .append('g')
      .attr('transform', `translate(0,${MARGIN})`)
      .call(d3.axisTop(x))
      .call(g => g.selectAll('.tick text').attr('font-size', FONT_SIZE))
      .call(g => g.select('.domain').remove());
    svg
      .append('g')
      .attr('transform', `translate(${MARGIN_LEFT},0)`)
      .call(d3.axisLeft(y).tickSizeOuter(0))
      .call(g => g.selectAll('.tick text').attr('font-size', FONT_SIZE));
  }, [data, height, width, handleClick]);

  useEffect(renderBarPlot, [renderBarPlot]);

  return (
    <div className="h-100">
      <Loader loading={loading}>
        <div className="customers tooltip p-3" ref={tooltipRef} />
        <svg ref={svgRef} />
      </Loader>
    </div>
  );
};

export default CustomerUnitMap;
